/**
 * How many people are here, and what they came to do.
 *
 * WHY A PAGE HAS TO SAY SO AT ALL
 * The site is static files served by Cloudflare's asset host, and the Worker is
 * only invoked for a path no file answers (see `worker/index.js`). So no request
 * for a page ever reaches code that could count it. That is the deployment
 * working as designed, and it means the count has to come from the page: one
 * `POST /beacon` on load, and one more when something worth knowing about
 * happens. `worker/index.js` turns each into a data point.
 *
 * WHAT IT SENDS, AND WHAT IT REFUSES TO
 * Nine short words: what happened, which section of the site, one detail (a game
 * id, an opponent's name from the ladder, `won`), the shape of the screen,
 * whether the site was opened as an installed app, the hostname that linked here,
 * and whether this browser has been here before. Not the URL, not the query
 * string, not the user agent, not a visitor id — see `worker/events.js` for the
 * long version of why, and for what these numbers therefore cannot tell you.
 *
 * `returning` is worth its own sentence, because it is the line this file walks.
 * A date is written into `localStorage` the first time a browser loads a page
 * here, and it never leaves the browser; what is sent is the word `new` or the
 * word `returning`. An id would be more useful and would also be a cookie in
 * everything but name — a durable handle for one person's visits, which is the
 * thing that turns "measuring a website" into "tracking people" and puts a
 * consent banner in front of a chess board. The word is enough to answer the
 * question anybody actually asks of it.
 *
 * AND THEN THE SITE ASKED FOR THE ID ANYWAY, for its own admin page at `/admin/`.
 * The paragraph above is still true of Analytics Engine — `worker/index.js` writes
 * it nothing new. But the same report now carries three random GUIDs: a visitor id
 * kept in `localStorage`, a session id that rolls over after half an hour with
 * nothing happening, and an id for this one page load. With them go the page's own
 * path (never its query string — a friend's room code lives there), its title, how
 * long it has been looked at, how far down it was scrolled, the browser's language,
 * timezone and screen size, and any `utm_` campaign tags it was opened with.
 * `worker/visits.js` keeps them in D1 and says exactly what "anonymous" means there.
 * That id is the durable handle described above, and so IT WAITS FOR CONSENT.
 *
 * ONLY THE ID-FREE HALF RUNS WITHOUT ASKING. `consent.js` puts the banner up; until
 * it says `granted`, this file writes nothing to the device and sends only the
 * Analytics Engine report — with `visitor: 'unknown'`, because even the "seen"
 * date above is storage used for analytics. Granting mid-page starts the ids and
 * GA on the spot; refusing, then or later, deletes the ids, the date and GA's
 * cookies. See the header of `consent.js` for where the line is drawn and why.
 *
 * A browser can opt itself out — the admin page has the switch, so the site's owner
 * is not most of the site's traffic. It sets `immortal-analytics-off` in
 * `localStorage`, and nothing below sends anything while it is there.
 *
 * THAT PARAGRAPH NOW DESCRIBES THE BEACON AND NOT THE SITE. Google Analytics was
 * added at the bottom of this file, and `_ga` is exactly the durable handle the
 * lines above decline to keep. The beacon still refuses one, which is why they
 * stand; the page they run on no longer does, which is why this sentence is here.
 * Whether a banner is owed follows from the cookie down there, not from anything
 * above it — and GA is now loaded only once the banner has been answered yes.
 *
 * IT CANNOT BREAK A PAGE, WHICH IS THE ONE HARD REQUIREMENT
 * Every path through here is wrapped, `sendBeacon` is fire-and-forget and returns
 * false rather than throwing, and there is no `await` anywhere: a page that fails
 * to report is a page with one fewer row in a report, and it must never be a page
 * that fails to play chess. `npm run dev` posts to a dev server that answers 204
 * and keeps nothing, and offline the post is dropped on the floor unqueued.
 */

/**
 * Where the Worker is listening. The `/chess-app` prefix is dropped at build time,
 * exactly as it is for the relay in `friend.js` — same rewrite, same reason, so
 * this URL is right under `npm run dev` and right deployed, knowing neither.
 */
const BEACON = '/beacon';

// Whether this browser has said yes to being remembered. See its header.
import { DENIED, GRANTED, onConsent, readConsent } from './consent.js';

/** The date this browser first loaded a page here. Never sent; see above. */
const SEEN_KEY = 'immortal-seen';

/**
 * Where the app is mounted locally, and WHY IT IS A STRING AND NOT A REGEX.
 *
 * `scripts/build-site.mjs` strips the `/chess-app` prefix out of every text file,
 * which is what makes one spelling of the beacon address above work in both
 * places. Write that prefix as a regex anchored at the start of a path, and the
 * source of the literal contains the sequence slash-chess-app-slash — the escaped
 * slash in front of it, and the closing slash of the regex itself — so the rewrite
 * eats it and the deployed file throws "Invalid regular expression" before it has
 * counted anything. The same line is perfectly valid locally, which is the worst
 * shape a bug can have in this project: right in every check that runs against
 * `npm run dev`, and wrong only on the live site.
 *
 * A string with no trailing slash cannot be rewritten, and `startsWith` needs no
 * escaping. `scripts/check-analytics.mjs` opens the built pages in a browser and
 * waits for the beacon, which is how the paragraph above stopped being a theory.
 */
const LOCAL_PREFIX = '/chess-app';

/**
 * Which part of the site a path belongs to.
 *
 * The `/chess-app` prefix is stripped at RUN time rather than by the build's text
 * rewrite, because this is the one place where the path is not written down in a
 * file — it is read out of `location` while the page is running, and locally that
 * really is that prefix and then `/play/vs-computer.html`. Everything else here the
 * rewrite can reach; this it cannot.
 */
function sectionOf(pathname) {
  const path = pathname.startsWith(LOCAL_PREFIX) ? pathname.slice(LOCAL_PREFIX.length) : pathname;
  if (path === '/' || path === '/index.html') return ['library', ''];
  if (path.startsWith('/watch/')) return ['watch', ''];
  if (path === '/play/vs-computer.html') return ['computer', ''];
  if (path === '/play/vs-friend.html') return ['friend', ''];
  if (path === '/play/lichess.html') return ['lichess', ''];
  if (path.startsWith('/puzzles/')) return ['puzzles', ''];
  if (path.startsWith('/guess/')) return ['guess', ''];
  if (path.startsWith('/daily/')) return ['daily', ''];
  if (path.startsWith('/news/')) return ['news', ''];
  if (path.startsWith('/study/')) return ['study', ''];
  if (path.startsWith('/learn/')) return ['learn', ''];
  // A reading page carries the game's id in its own name, and that id is the most
  // useful thing on this whole list: it is which of the hundred games search
  // engines are actually sending people to.
  const game = /^\/games\/([a-z0-9-]+)\.html$/.exec(path);
  if (game) return ['game', game[1]];
  return ['other', ''];
}

/** phone, tablet or desktop, from the two things a browser will say for certain. */
function device() {
  try {
    const coarse = matchMedia('(pointer: coarse)').matches;
    if (!coarse) return 'desktop';
    const short = Math.min(innerWidth, innerHeight);
    return short < 500 ? 'phone' : 'tablet';
  } catch {
    return 'desktop';
  }
}

/**
 * Whether this is the installed app rather than a tab.
 *
 * The install button, the manifest and the service worker are a third of the work
 * in `install.js` and `sw.js`, and until now nothing here could say whether any of
 * it gets used. This is that answer.
 */
function mode() {
  try {
    return matchMedia('(display-mode: standalone)').matches
      || navigator.standalone === true ? 'app' : 'browser';
  } catch {
    return 'browser';
  }
}

/** The hostname that linked here — never the path, which is where names hide. */
function referrer() {
  try {
    if (!document.referrer) return 'direct';
    const host = new URL(document.referrer).hostname;
    return host === location.hostname ? 'internal' : host;
  } catch {
    return 'direct';
  }
}

/**
 * `new` on a browser that has not been here, `returning` after that.
 *
 * `unknown` when storage throws, which is private mode and an iframe with storage
 * blocked — and when consent has not been given, which is now most of it. Not
 * treated as `new`: counting every such visit as a first visit would quietly
 * overstate the one number this is for.
 */
function visitor() {
  if (readConsent() !== GRANTED) return 'unknown';
  try {
    const seen = localStorage.getItem(SEEN_KEY);
    if (seen) return 'returning';
    localStorage.setItem(SEEN_KEY, new Date().toISOString().slice(0, 10));
    return 'new';
  } catch {
    return 'unknown';
  }
}

/** The switch the admin page sets on the owner's own browsers. */
const OFF_KEY = 'immortal-analytics-off';

/** The anonymous visitor id, and the session with the time it was last active. */
const VISITOR_KEY = 'immortal-visitor';
const SESSION_KEY = 'immortal-session';

/** Half an hour with nothing sent, and the next page starts a new session. */
const SESSION_IDLE = 30 * 60 * 1000;

function optedOut() {
  try {
    return localStorage.getItem(OFF_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * A random v4 GUID. `crypto.randomUUID` only exists in a secure context, and the
 * dev server on a LAN address is not one, so the fallback formats the same random
 * bytes by hand.
 */
function uuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * This browser's visitor id, made on its first visit.
 *
 * Null when storage throws — private mode, a sandboxed iframe. NOT a throwaway id
 * instead: an id that changes on every page would count one person as ten, and
 * the report still reaches Analytics Engine without one.
 */
function visitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id || !/^[0-9a-f-]{36}$/.test(id)) {
      id = uuid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/** The current session id, renewed after `SESSION_IDLE`, and touched on every report. */
function sessionId() {
  try {
    const now = Date.now();
    let session = null;
    try { session = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { /* new */ }
    if (!session || typeof session.id !== 'string' || !(now - session.last < SESSION_IDLE)) {
      session = { id: uuid(), last: now };
    }
    session.last = now;
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session.id;
  } catch {
    return null;
  }
}

/**
 * The campaign a link was tagged with. Only the three `utm_` tags worth grouping
 * by, never the rest of the query; a Google Ads click (`gclid`) with no tags of its
 * own is filed as google / cpc, and the click id itself is not kept.
 */
function campaign() {
  try {
    const q = new URLSearchParams(location.search);
    const utm = {
      source: q.get('utm_source') || '',
      medium: q.get('utm_medium') || '',
      campaign: q.get('utm_campaign') || '',
    };
    if (!utm.source && q.has('gclid')) Object.assign(utm, { source: 'google', medium: 'cpc' });
    return utm.source || utm.campaign ? utm : null;
  } catch {
    return null;
  }
}

/** The page's path as the deployed site spells it, without the query. */
function sitePath(pathname) {
  const path = pathname.startsWith(LOCAL_PREFIX) ? pathname.slice(LOCAL_PREFIX.length) : pathname;
  return (path || '/').slice(0, 200);
}

/** Settled once per page rather than per event: nothing in it changes mid-visit. */
const WHO = (() => {
  try {
    if (optedOut()) return null;
    const [section, detail] = sectionOf(location.pathname);
    return {
      section,
      pageDetail: detail,
      device: device(),
      mode: mode(),
      referrer: referrer(),
      visitor: visitor(),
      // What a visit adds, once there is consent to remember one. Read from this
      // page and held in memory; nothing here is stored or sent before `startVisit`.
      path: sitePath(location.pathname),
      lang: navigator.language || '',
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      screen: `${Math.round(screen.width)}x${Math.round(screen.height)}`,
      utm: campaign(),
    };
  } catch {
    return null;
  }
})();

/**
 * The visitor id and this page load's id — null until consent, and again after it
 * is withdrawn. `count()` attaches the visit fields only while this is set.
 */
let visit = null;

/** Start remembering this browser. Returns whether a visit began just now. */
function startVisit() {
  if (visit || !WHO || optedOut()) return false;
  const v = visitorId();
  if (!v) return false;
  visit = { v, pv: uuid() };
  return true;
}

/** Stop, and delete everything that remembered it. */
function stopVisit() {
  visit = null;
  try {
    for (const key of [VISITOR_KEY, SESSION_KEY, SEEN_KEY]) localStorage.removeItem(key);
  } catch { /* nothing could have been stored either */ }
}

// ---------------------------------------------------------------- time on page

/**
 * How long this page has actually been looked at, in milliseconds.
 *
 * Counted in ticks rather than as "now minus when it opened", because the second
 * number is mostly lies: a tab left in the background all afternoon, a laptop
 * closed overnight. A tick only counts while the page is visible AND something has
 * happened on it within `IDLE` — long enough that watching a whole game replay
 * without touching anything still counts, short enough that a board left on a
 * screen does not. Each tick is capped, so a timer the browser throttled or a
 * machine that slept adds at most one tick's worth.
 *
 * Measured from the moment the page opened, whether or not there is consent yet —
 * it is a number in memory, and it only leaves the page inside a report sent after
 * a yes. Somebody who reads for a minute and then answers the banner has still
 * read for a minute.
 */
const TICK = 5000;
const IDLE = 30 * 60 * 1000;
let activeMs = 0;
let lastTick = performance.now();
let lastActivity = lastTick;
let maxScroll = 0;
let sentMs = -1;

function tick() {
  const now = performance.now();
  if (document.visibilityState === 'visible' && now - lastActivity < IDLE) {
    activeMs += Math.min(now - lastTick, TICK * 3);
  }
  lastTick = now;
}

/** The furthest down the page has been scrolled, as a percentage of its height. */
function measureScroll() {
  try {
    const doc = document.documentElement;
    const height = Math.max(doc.scrollHeight, document.body?.scrollHeight || 0);
    const seen = height > innerHeight ? ((scrollY + innerHeight) / height) * 100 : 100;
    maxScroll = Math.max(maxScroll, Math.min(100, Math.round(seen)));
  } catch { /* a report is never worth an exception on a chess page */ }
}

if (WHO) {
  try {
    setInterval(tick, TICK);
    const active = () => { lastActivity = performance.now(); };
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
      addEventListener(type, active, { passive: true, capture: true });
    }
    let moved = 0;
    addEventListener('pointermove', () => {
      const now = performance.now();
      if (now - moved > 1000) { moved = now; active(); }
    }, { passive: true });
    let scrolling = false;
    addEventListener('scroll', () => {
      active();
      if (scrolling) return;
      scrolling = true;
      requestAnimationFrame(() => { scrolling = false; measureScroll(); });
    }, { passive: true });
    addEventListener('load', measureScroll);

    // Sent when the page is hidden, which on a phone is the last moment a page is
    // reliably allowed to say anything — `pagehide` is the backstop for desktop
    // browsers that close a tab without hiding it first. Coming back and leaving
    // again sends the new total; the Worker keeps the larger.
    const flush = () => {
      if (!visit) return;
      tick();
      measureScroll();
      const seconds = Math.round(activeMs / 1000);
      if (seconds === sentMs) return;
      sentMs = seconds;
      count('page-time', '', seconds);
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
      else { lastTick = performance.now(); lastActivity = lastTick; }
    });
    addEventListener('pagehide', flush);
  } catch { /* a report is never worth an exception on a chess page */ }
}

/**
 * Report one event. `count('play-end', 'won', 41)`.
 *
 * The name has to be in `EVENTS` in `worker/events.js` or the Worker drops it with
 * a 400 — and `check-analytics.mjs` reads the calls in this directory against that
 * list, so a name that is not allowed is a failing check rather than a silent hole
 * in a report. Detail and value are both optional; a bare `count('page')` is fine.
 *
 * @param {string} event   one of `EVENTS`
 * @param {string} [detail] a game id, an outcome, an opponent — bounded to 64
 *                          characters by the Worker
 * @param {number} [value]  the one number worth keeping about this event
 */
export function count(event, detail = '', value = 0) {
  if (!WHO) return;
  try {
    // A page opened from a file:// URL has no origin to post to, and the two
    // capture scripts in the studio open the animation pages that way.
    if (location.protocol !== 'http:' && location.protocol !== 'https:') return;
    const report = {
      event,
      section: WHO.section,
      detail: String(detail ?? '').slice(0, 64),
      device: WHO.device,
      mode: WHO.mode,
      referrer: WHO.referrer,
      visitor: WHO.visitor,
      value: Number.isFinite(value) ? value : 0,
    };
    if (visit) {
      const s = sessionId();
      if (s) {
        Object.assign(report, {
          v: visit.v,
          s,
          pv: visit.pv,
          path: WHO.path,
          title: document.title.slice(0, 120),
          lang: WHO.lang,
          tz: WHO.tz,
          screen: WHO.screen,
          ms: Math.round(activeMs),
          scroll: maxScroll,
          ...(WHO.utm ? { utm: WHO.utm } : {}),
        });
      }
    }
    const body = JSON.stringify(report);
    // `sendBeacon` and not `fetch`, for the one property it has that matters: the
    // browser owns the request from here, so it survives the page being closed —
    // which is when half of these are sent, `play-end` most of all.
    navigator.sendBeacon?.(BEACON, body);
  } catch { /* a report is never worth an exception on a chess page */ }
}

/**
 * The page view, sent by the act of loading this file.
 *
 * Which is why every page includes it with a plain `<script type="module">` tag
 * and the pages that also want `count()` import it: a module is evaluated once, so
 * both spellings share this one line and no page can report itself twice.
 */
if (WHO) {
  if (readConsent() === GRANTED) startVisit();
  count('page', WHO.pageDetail);
}

/**
 * The banner being answered while this page is open.
 *
 * A yes starts the visit here and now, and reports this page as its first view
 * with `page-time` — which the Worker writes to the visit store only, so Analytics
 * Engine, which already counted this page at load, does not count it twice.
 */
onConsent((choice) => {
  if (choice === GRANTED) {
    if (startVisit()) count('page-time', '', Math.round(activeMs / 1000));
    google();
  } else if (choice === DENIED) {
    stopVisit();
    forgetGoogle();
  }
});


// --------------------------------------------------------------------- Google

/**
 * Google Analytics 4, added alongside the beacon and not instead of it.
 *
 * WHY BOTH, WHEN THE BEACON WAS THE WHOLE POINT
 * The beacon above answers questions about THIS site that no general tool can:
 * which of the hundred games a search engine actually sends people to, whether a
 * game against the ladder was played out or abandoned, whether the installed app
 * gets opened. It cannot answer the ordinary ones — how many people, from where,
 * over which weeks, against last month — because answering those needs a durable
 * handle for one visitor and the beacon deliberately refuses to keep one. GA is
 * here for that second half, and `_ga` is that handle. See the note at the top of
 * this file: what changed is the site, not the beacon.
 *
 * THE TAG IS LOADED FROM HERE RATHER THAN PASTED INTO NINE HEADS
 * Six of the site's pages are hand-written HTML and three are rendered by
 * `pages.mjs` — and all nine already carry this file, so this is the one place
 * that reaches every page and cannot fall out of step with the others. The lines
 * below do exactly what Google's snippet does, in the order it does it: the
 * `dataLayer` queue is filled first and `gtag.js` drains it whenever it arrives,
 * which is why loading the script asynchronously costs nothing.
 *
 * NOT FROM LOCALHOST, which is the one deliberate difference from the snippet.
 * `npm run dev` and `npm run cf:dev` would otherwise file every reload of a page
 * being worked on into the same property as real visits. That is the noise GA's
 * own "internal traffic" filter exists to remove, and that filter wants a
 * dashboard rule and a stable IP; a hostname test wants neither. The cost is that
 * GA cannot be confirmed locally — the check is the Realtime report with the
 * deployed site open, and `check-analytics.mjs` still covers the beacon in both
 * places.
 */
const GA_ID = 'G-LQTRGJ169E';

/** The hosts `npm run dev` and `npm run cf:dev` serve on, and nothing else. */
const DEV_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/** Whether gtag.js has been put on this page. */
let googleLoaded = false;

function google() {
  try {
    if (readConsent() !== GRANTED) return;
    // Same two guards the beacon takes, for the same two reasons: a `file://`
    // page has no origin to report from, and the studio's capture scripts open
    // the animation pages that way.
    if (location.protocol !== 'http:' && location.protocol !== 'https:') return;
    if (DEV_HOSTS.has(location.hostname)) return;
    // The owner's own browsers, switched off from the admin page. See the top.
    if (optedOut()) return;
    // Withdrawn and then given again on the same page: GA reads this flag on
    // every hit, so clearing it is all it takes to resume.
    window[`ga-disable-${GA_ID}`] = false;
    if (googleLoaded) return;
    googleLoaded = true;

    // The queue before the tag, so nothing is lost in the case where the script
    // is already in cache and runs the moment it is appended.
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    // Consent Mode, said explicitly: analytics was agreed to and advertising was
    // never asked about, so Google is told not to use this visit for ads.
    window.gtag('consent', 'default', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    window.gtag('config', GA_ID);

    // THE TAG ITSELF WAITS FOR THE PAGE TO BE DONE LOADING.
    //
    // `async` keeps it off the render path but not out of the way: it is ~95KB
    // over a fresh DNS lookup, TLS handshake and connection to somebody else's
    // origin, and it starts all of that while the board is still fetching. The
    // queue above is the whole reason this can wait — `gtag()` is defined and
    // pushing to `dataLayer` from this line onwards, so every hit made before the
    // script lands is replayed when it does, and nothing is lost by being late.
    const add = () => {
      const tag = document.createElement('script');
      tag.async = true;
      tag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(tag);
    };
    const idle = () => (window.requestIdleCallback
      ? window.requestIdleCallback(add, { timeout: 3000 })
      : setTimeout(add, 1200));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  } catch { /* a report is never worth an exception on a chess page */ }
}

/**
 * Consent withdrawn: GA stops sending (its documented opt-out flag, honoured on
 * every hit) and its cookies are deleted. They are set on the widest domain GA can
 * write — `.chesszz.com` — so each is expired there, on the bare host, and at every
 * level in between.
 */
function forgetGoogle() {
  try {
    window[`ga-disable-${GA_ID}`] = true;
    const parts = location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join('.')}`);
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.split('=')[0].trim();
      if (!/^_ga(_|$)/.test(name)) continue;
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
  } catch { /* a report is never worth an exception on a chess page */ }
}

google();
